<template>
  <section
    v-if="formItinerary && formItinerary[0]"
    :class="focusedGuideBoxClass('table-box mt-5 p-5 BasicInfoForm', 3, true)"
  >
    <div
      class="columns is-flex is-justify-content-space-between w-100 m-0 mb-4"
    >
      <h2 class="is-h2 mr-2 pb-3">
        <strong> Basic Information | </strong>
        <span v-if="!creatingNewQuote" class="quoteCreationSubtitle">
          Quote creation:
          {{ itinerary.data.created_date | customDate(datesFormat) }}
        </span>
        <span v-else> Creating a new {{ itineraryTypeLabel }} </span>
        <BaseSelect
          label="Type"
          :list="itineraryTypes"
          option-value="id"
          option-text="name"
          placeholder="Select a itinerary type"
          :disabled="loading || disableQuoteByStatus || hideElementByCondition"
          :value="formItinerary[0].itinerary_type_id || 0"
          class="field mb-2 itinerary_type_top__field"
          size="large"
          :data-test-id="constants.DATA_TEST_ID_VALUES.BASIC_INFO_TYPE_SELECT"
          @input="updateformItinerary('itinerary_type_id', $event)"
        />
      </h2>

      <AddServicesModal
        class="basicInfoAdd"
        :itinerary-type-label="itineraryTypeLabel"
        :handle-save-itinerary="handleSaveItinerary"
        :handle-modal-shortcuts="handleModalShortcuts"
        :handle-modal-prevent-basic-info="handleModalPreventBasicInfo"
        :modal-prevent-basic-info="modalPreventBasicInfo"
        :show-services="showServices"
        :set-loading="setLoading"
        :shortcuts-open="shortcutsOpen"
        :is-set-assignee-modal-open="isSetAssigneeModalOpen"
      />
    </div>
    <div class="columns">
      <GuideBox
        title="Creating a new quote"
        description="Choose a client"
        :step="4"
        :position-y="-180"
        :show-next-button="formItinerary[0].client_id !== null"
      />
      <div
        v-if="authConfig.clientDisplayEnabled"
        :class="focusedGuideBoxClass('column', 4, true)"
      >
        <BaseSelect
          size="large"
          label="Client*"
          :list="clients"
          option-value="id"
          option-text="name"
          :disabled="
            loading ||
            disableQuoteByStatus ||
            hideElementByCondition ||
            Boolean(request.id)
          "
          placeholder="Select a client"
          :value="formItinerary[0].client_id"
          :data-test-id="constants.DATA_TEST_ID_VALUES.BASIC_INFO_CLIENT_SELECT"
          :is-not-valid="fieldHasError('client_id')"
          @input="
            updateClient($event);
            nextStep();
          "
        />
      </div>
      <GuideBox
        title="Creating a new quote"
        description="Write a title for your quote"
        :step="5"
        :position-y="-180"
        :show-next-button="formItinerary[0].title.value !== null"
      />
      <div :class="focusedGuideBoxClass('column', 5, true)">
        <BaseInput
          ref="form_title__element"
          size="large"
          label="Title*"
          :disabled="disableQuoteByStatus || loading || hideElementByCondition"
          :value="formItinerary[0].title.value || ''"
          placeholder="Title"
          :data-test-id="constants.DATA_TEST_ID_VALUES.BASIC_INFO_TITLE"
          :is-not-valid="fieldHasError('title', 'value')"
          @input.native="validateTitleInput"
        />
      </div>
      <GuideBox
        title="Creating a new quote"
        description="Choose the number of passengers that you want"
        :step="6"
        :position-y="-180"
        :show-next-button="formItinerary[0].num_pax !== null"
      />
      <GuideBox
        title="Creating a new quote"
        description="Set the room distribution that you like"
        :step="7"
        :position-y="-220"
        :position-x="-250"
        :overlay="false"
        :show-next-button="false"
        :show-back-button="false"
      />
      <div :class="focusedGuideBoxClass('column', 6, true)">
        <BaseSelect
          size="large"
          :list="numPax"
          option-value="value"
          option-text="value"
          placeholder="Select pax qty."
          :value="formItinerary[0].num_pax"
          label="N° Pax*"
          :disabled="disableQuoteByStatus || loading || hideElementByCondition"
          :data-test-id="constants.DATA_TEST_ID_VALUES.BASIC_INFO_PAX_SELECT"
          :is-not-valid="fieldHasError('num_pax')"
          data-action-type="BASIC_INFO:NUM_PAX_CHANGE"
          @input="
            nextStep();
            updateformItinerary('num_pax', $event);
            setDefaultRoomDistribution();
          "
        />
      </div>
      <div v-if="authConfig.roomDistributionEnabled" class="column">
        <BaseInputRoom
          size="large"
          label="Room Distribution *"
          :disabled="disableQuoteByStatus || loading || hideElementByCondition"
          placeholder="Select room distribution"
          :data="roomPreferableDistributionJson"
          data-action-type="BASIC_INFO:ROOM_DISTRIBUTION_CHANGE"
          @click.native="handleModalRoomDistribution"
        >
          <b-icon slot="icon" icon="bed" alt="icon" class="card-icon"></b-icon>
        </BaseInputRoom>

        <BaseModal
          :is-component-modal-active="modalRoomDistribution"
          :close-modal="handleModalRoomDistribution"
        >
          <div
            slot="body"
            class="roomDistributionCard is-flex align-items-center"
          >
            <h2 class="roomDistributionTitle">
              <b>Distribution of Rooms</b>
            </h2>
            <span class="roomDistributionSubtitle">
              Please select the preferred Distribution of Rooms
              <b>{{ formItinerary[0].num_pax }}</b> passengers
            </span>
            <span class="roomDistributionSubtitle">
              Keep in mind that Room Distribution could vary according to the
              availability of the hotel.
            </span>
            <div
              class="is-flex roomDistributionBody"
              :data-test-id="
                constants.DATA_TEST_ID_VALUES.BASIC_INFO_ROOM_SELECT
              "
            >
              <div
                v-for="item in roomPreferableDistributionJson"
                :key="item.id"
                class="roomDistributionBodyItem is-flex align-items-center"
                :is-selected="item.qty > 0 ? 'active' : ''"
              >
                <div>
                  <IconBedSingle
                    v-if="
                      item.id_room_type === 1 ||
                      item.id_room_type === 4 ||
                      item.id_room_type === 3
                    "
                  ></IconBedSingle>
                  <IconBedSingle v-if="item.id_room_type === 3"></IconBedSingle>
                  <IconBedDouble
                    v-if="
                      item.id_room_type === 2 ||
                      item.id_room_type === 5 ||
                      item.id_room_type === 4
                    "
                  ></IconBedDouble>
                  <IconBedDouble v-if="item.id_room_type === 5"></IconBedDouble>
                </div>
                <span>{{ item.name }}</span>
                <BaseInputNumber
                  v-model="item.qty"
                  controls-position="compact"
                  size="is-small"
                  :max="item.max_qty"
                  :min="0"
                  :placeholder="0"
                  :disabled="item.max_qty === 0 ? true : false"
                  @input="setMaxRoomQty"
                >
                </BaseInputNumber>
              </div>
            </div>
          </div>

          <div slot="buttons" class="roomDistributionButtons is-flex">
            <BaseButton
              action="secondary"
              size="large"
              class=""
              @click="
                prevStep();
                handleModalRoomDistribution();
              "
            >
              <p slot="text">Cancel</p>
            </BaseButton>
            <BaseButton
              action="tercery"
              size="large"
              :disabled="loading || remainingPax > 0"
              :data-test-id="
                constants.DATA_TEST_ID_VALUES.BASIC_INFO_ROOM_SELECT_SAVE
              "
              @click="
                saveRoomPreferedDistribution();
                nextStep();
              "
            >
              <p slot="text">Continue</p>
            </BaseButton>
          </div>
        </BaseModal>
      </div>
    </div>
    <div class="columns">
      <GuideBox
        title="Creating a new quote"
        description="Choose a date to start the itinerary"
        :step="8"
        :position-y="-20"
        :position-x="280"
      />
      <div
        :class="focusedGuideBoxClass('column', 8, true)"
        :data-test-id="
          constants.DATA_TEST_ID_VALUES.BASIC_INFO_DATA_FROM_SELECT
        "
      >
        <BaseDatePicker
          label="Date from*"
          :value="dateFrom"
          :disabled="disableQuoteByStatus || loading || hideElementByCondition"
          placeholder="Select date from"
          :is-not-valid="fieldHasError('start_date')"
          @input="dateFrom = $event"
        />
      </div>
      <GuideBox
        title="Creating a new quote"
        description="Choose a date to finish the itinerary"
        :step="9"
        :position-y="-20"
        :position-x="280"
      />
      <div
        :class="focusedGuideBoxClass('column', 9, true)"
        :data-test-id="constants.DATA_TEST_ID_VALUES.BASIC_INFO_DATA_TO_SELECT"
      >
        <BaseDatePicker
          label="Date to *"
          :value="dateTo"
          :min="minDateTo"
          :max="maxDateTo"
          :disabled="disableQuoteByStatus || loading || hideElementByCondition"
          placeholder="Select date to"
          :is-not-valid="fieldHasError('end_date')"
          @input="dateTo = $event"
        />
      </div>
      <GuideBox
        title="Creating a new quote"
        description="Here you have the number of days included in the itinerary"
        :step="10"
        :position-y="-150"
      />
      <div :class="focusedGuideBoxClass('column', 10, true)">
        <BaseInput
          id="daysInput"
          size="large"
          label="N° Day(s)"
          :disabled="disableQuoteByStatus || loading || hideElementByCondition"
          placeholder="N° Days(s)"
          :value="days || 1"
          type="number"
          :max="constants.MAX_ITINERARY_DAYS"
          :maxlength="2"
          :is-not-valid="fieldHasError('num_day')"
          @input.native="handleValidateNDays($event)"
        />
      </div>
      <div v-if="authConfig.tripTypeEnabled" class="column">
        <BaseSelect
          size="large"
          :list="tripTypes"
          option-value="id"
          option-text="name"
          :disabled="true || disableQuoteByStatus || hideElementByCondition"
          placeholder="Select a trip type"
          :value="formItinerary[0].trip_type || ''"
          label="Trip type*"
          @input="updateformItinerary('trip_type', $event)"
        />
      </div>
    </div>
    <div class="columns">
      <GuideBox
        title="Creating a new quote"
        description="Choose a contract"
        :step="11"
        :position-y="-150"
        :show-next-button="formItinerary[0].contract_id !== null"
      />
      <div
        v-if="authConfig.contractClientDisplayEnabled"
        :class="focusedGuideBoxClass('column is-relative', 11, true)"
      >
        <b-tooltip
          v-if="!creatingNewQuote"
          position="is-top"
          label="View Sales Manuals"
          class="corner__icon"
        >
          <button
            :disabled="loading"
            @click="displayIsViewContractSalesManualModal"
          >
            <IconEye
              class="icon color-8 is-xsmall mr-1"
              style="width: 1rem; height: 1rem"
            />
          </button>
        </b-tooltip>
        <BaseSelect
          size="large"
          :list="client_contracts"
          option-value="id"
          option-text="code"
          :disabled="
            loading ||
            disableQuoteByStatus ||
            hideElementByCondition ||
            formItinerary[0].client_id === null ||
            Boolean(request.id)
          "
          placeholder="Select a contract"
          :value="formItinerary[0].contract_id"
          label="Contract*"
          :data-test-id="
            constants.DATA_TEST_ID_VALUES.BASIC_INFO_CONTRACT_SELECT
          "
          :is-not-valid="fieldHasError('contract_id')"
          @input="
            updateformItinerary('contract_id', $event);
            updateContractLocations($event);
            nextStep();
          "
          @click.native="isContractAvailableToSelect"
        />
      </div>
      <div class="column">
        <BaseInput
          id="daysInput"
          size="large"
          label="Code*"
          :disabled="
            itinerary.code === null ||
            disableQuoteByStatus ||
            loading ||
            hideElementByCondition
          "
          placeholder="Code-123-ABC"
          :value="formItinerary[0].code || ''"
          @input="updateformItinerary('code', $event)"
        />
      </div>
      <div v-if="isProVersion" class="column">
        <BaseSelect
          size="large"
          :list="currencies"
          option-value="id"
          option-text="currency_code"
          :disabled="
            true || disableQuoteByStatus || loading || hideElementByCondition
          "
          placeholder="Select a currency"
          :value="formItinerary[0].currency_id"
          label="Currency*"
          @input="updateformItinerary('currency_id', $event)"
        />
      </div>
      <div class="column">
        <BaseSelect
          size="large"
          :list="languages"
          option-value="id"
          option-text="language_name"
          :data-feature="featureName.LANGUAGE_SELECTOR"
          :disabled="disableQuoteByStatus || loading || hideElementByCondition"
          placeholder="Select a language"
          :value="formItinerary[0].language_id || ''"
          label="Main Language*"
          @input="updateFormLanguage($event)"
        />
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <BaseSelect
          label="Type"
          :list="itineraryTypes"
          option-value="id"
          option-text="name"
          placeholder="Select a itinerary type"
          :disabled="loading || disableQuoteByStatus || hideElementByCondition"
          :value="formItinerary[0].itinerary_type_id || 0"
          class="field itinerary_type_bottom__field"
          size="large"
          :data-test-id="constants.DATA_TEST_ID_VALUES.BASIC_INFO_TYPE_SELECT"
          :is-not-valid="fieldHasError('itinerary_type_id')"
          @input="updateformItinerary('itinerary_type_id', $event)"
        />
      </div>

      <div v-if="displayQuoteIsPublicField" class="column">
        <BaseSelect
          label="Public Status"
          :list="quotePublicStatuses"
          option-value="id"
          option-text="name"
          placeholder="Select a Public status"
          :disabled="loading || disableQuoteByStatus || hideElementByCondition"
          :value="formItinerary[0].itinerary_public || 0"
          class="field itinerary_type_bottom__field"
          size="large"
          @input="updateformItinerary('itinerary_public', $event)"
        />
      </div>

      <div class="column">
        <div class="assignee_container is-relative">
          <label class="label mb-1" style="font-size: 1em">Assignee</label>
          <div
            class="p-2"
            :disabled="disableQuoteByStatus"
            :data-feature="featureName.OWNER_ASSIGNEE"
            @click="openSetAssigneeModal"
          >
            <b-icon
              icon="account"
              size="is-small"
              custom-size="mdi-18px"
              class="has-text-grey-light"
            ></b-icon>

            <p v-if="formItinerary[0].owner && formItinerary[0].owner.name">
              {{ formItinerary[0].owner.name }}
            </p>

            <p v-else class="has-text-grey-light">No assignee selected</p>
          </div>
        </div>

        <SetAssigneeModal
          :title="`Assign Quote to User`"
          :set-loading="setLoading"
          :is-set-assignee-modal-open="isSetAssigneeModalOpen"
          :handle-item-click="preAssignQuoteToUser"
          @update:isSetAssigneeModalOpen="isSetAssigneeModalOpen = $event"
        />
      </div>

      <div v-if="isAppTenant" class="column">
        <BaseDatePicker
          label="Expiration Date"
          :value="expirationDate"
          :min="dateTo"
          :max="maxExpirationDate"
          :disabled="disableQuoteByStatus || loading || hideElementByCondition"
          :is-not-valid="fieldHasError('expiration_date')"
          placeholder="Select expiration date"
          @input="expirationDate = $event"
        />
      </div>

      <div class="column">
        <BaseInput
          size="large"
          label="Client Booking Reference"
          :disabled="disableQuoteByStatus || loading || hideElementByCondition"
          placeholder="Client Booking Reference"
          :value="formItinerary[0].reference_code || ''"
          @input="updateformItinerary('reference_code', $event)"
        />
      </div>
    </div>

    <div
      class="columns is-flex is-justify-content-space-between is-align-items-center basic_info_form__footer mt-3"
    >
      <div>
        <BaseShortcutLink class="shortcutsLink" @click="handleModalShortcuts" />
        <BaseModal
          :is-component-modal-active="shortcutsOpen"
          type="shortcuts"
          :shortcuts="shortcuts"
          :close-modal="handleModalShortcuts"
        >
          <span slot="title">Basic Information keyboard shortcuts </span>
        </BaseModal>
      </div>
    </div>
  </section>
</template>

<script>
import { parse, format } from "date-fns";
import { mapGetters } from "vuex";
import AddServicesModal from "@/components/quote-views/_shared/AddServicesModal.vue";
import SetAssigneeModal from "@/components/quotes/SetAssigneeModal.vue";
import { basicInformationShortcuts as shortcuts } from "@/utils/shortcuts";
import GuideBox from "../../_shared/GuideBox.vue";
import {
  MAX_ITINERARY_DAYS,
  DATE_FORMAT_YYYY_MM_DD,
  USER_ROLE,
  FEATURE_NAME,
} from "@/constants";
import { DATA_TEST_ID_VALUES } from "@/constants/test";
import helpers from "@/helpers";
import { ITINERARY_TYPES } from "@/constants/itinerary";
import { helper } from "@/utils/itinerary";
import { datesHelper } from "@//utils/dates";
import { isAppTenant } from "@/utils/utils";

export default {
  name: "BasicInfoForm",
  components: {
    AddServicesModal,
    GuideBox,
    SetAssigneeModal,
  },
  data() {
    return {
      isAppTenant: isAppTenant(),
      featureName: FEATURE_NAME,
      helper: {
        ...datesHelper,
        ...helper,
      },
      constants: {
        MAX_ITINERARY_DAYS,
        DATA_TEST_ID_VALUES,
        DATE_FORMAT_YYYY_MM_DD,
      },
      open: false,
      dateFrom: "",
      dateTo: this.formItinerary?.[0]?.end_date,
      maxDateTo: null,
      minDateTo: null,
      expirationDate: null,
      maxExpirationDate: null,
      numPax: helper.generateList(1, 40),
      shortcutsOpen: false,
      shortcuts,
      modalRoomDistribution: null,
      remainingPax: 0,
      isSetAssigneeModalOpen: false,
    };
  },
  watch: {
    clients(items) {
      this.checkItineraryClient(items);
    },
    itineraryTypeIdByQuery(newValue) {
      if (newValue && !this.itinerary.data?.id) {
        this.updateformItinerary("itinerary_type_id", newValue);
      }
    },
    dateFrom(newDateFrom) {
      if (!newDateFrom) return;

      const endDate = parse(newDateFrom);
      endDate.setDate(endDate.getDate() + this.days - 1);

      this.updateformItinerary(
        "end_date",
        format(endDate, DATE_FORMAT_YYYY_MM_DD)
      );
      this.updateformItinerary("start_date", newDateFrom);

      this.minDateTo = this.dateFrom;
      this.maxDateTo = this.getMaxDateTo(this.dateFrom);

      if (parse(newDateFrom) > parse(this.formItinerary[0].end_date)) {
        this.$buefy.toast.open({
          duration: 4000,
          message: `Incorrect dates: The end date cannot be before the start date.`,
          position: "is-top",
          type: "is-danger",
        });
        return false;
      }
    },
    dateTo(newDateTo) {
      if (!newDateTo) return;

      this.updateformItinerary("end_date", newDateTo);

      const endDate = parse(newDateTo);
      this.maxExpirationDate = new Date(
        endDate.getTime() + 1000 * 60 * 60 * 24 * 365
      );

      this.updateformItinerary(
        "expiration_date",
        format(this.maxExpirationDate, DATE_FORMAT_YYYY_MM_DD)
      );

      if (!this.formItinerary[0].start_date) {
        this.updateformItinerary(
          "start_date",
          this.helper.getDateWithAddedDays(newDateTo, 0)
        );
      }
      if (parse(newDateTo) < parse(this.formItinerary[0].start_date)) {
        this.$buefy.toast.open({
          duration: 4000,
          message: `Incorrect dates: The end date cannot be before the start date.`,
          position: "is-top",
          type: "is-danger",
        });
        return false;
      }
    },
    expirationDate(newDate) {
      if (!newDate) return;

      this.updateformItinerary("expiration_date", newDate);
    },
    formItinerary: {
      handler(newFormData) {
        this.dateFrom = newFormData[0].start_date;
        this.dateTo = newFormData[0].end_date;
        this.expirationDate = newFormData[0].expiration_date;
        this.validateQuoteIsLinkedToRequest();
        this.checkItineraryClient(this.clients);
      },
      deep: true,
    },
  },
  computed: {
    creatingNewQuote() {
      return !(
        this.itinerary.data &&
        this.itinerary.data.id &&
        this.formItinerary &&
        this.formItinerary.length &&
        this.formItinerary[0].quote_creation
      );
    },
    quotePublicStatuses() {
      return helper.getQuotePublicStatusesMapped();
    },
    displayItineraryTypes() {
      return helpers.hasAnyRoles(
        this.authData.user,
        [USER_ROLE.ADMIN, USER_ROLE.SUPPORT],
        true
      );
    },
    displayQuoteIsPublicField() {
      return (
        this.formItinerary?.[0]?.itinerary_type_id ===
          ITINERARY_TYPES.TEMPLATE.id && this.displayItineraryTypes
      );
    },
    sampleFormat() {
      const dtf = new Intl.DateTimeFormat(this.locale, { timezome: "UTC" });
      return dtf.format(new Date(2000, 11, 25, 12));
    },
    days() {
      const time =
        parse(this.formItinerary[0].end_date) -
        parse(this.formItinerary[0].start_date);
      const numberDays = time >= 0 ? time / (1000 * 60 * 60 * 24) + 1 : 0;
      this.handleChangeDays(numberDays);
      return Math.ceil(numberDays);
    },
    itineraryTypes() {
      const itineraryTypes = helper.getItineraryTypesMapped();
      const allowed = [];
      if (this.isOnlyTravelAgent) {
        allowed.push(ITINERARY_TYPES.NO_TEMPLATE.id);
        allowed.push(ITINERARY_TYPES.TEMPLATE.id);
        allowed.push(ITINERARY_TYPES.EXPERIENCE.id);
      } else if (isAppTenant()) {
        allowed.push(ITINERARY_TYPES.NO_TEMPLATE.id);
        allowed.push(ITINERARY_TYPES.TEMPLATE.id);
        allowed.push(ITINERARY_TYPES.MODULE.id);
      }

      return itineraryTypes.filter((it) => {
        if (allowed.length === 0) return true;
        return allowed.includes(it.id);
      });
    },
    isOnlyTravelAgent() {
      return helpers.hasAnyRoles(
        this.authData.user,
        [USER_ROLE.TRAVEL_AGENT],
        false
      );
    },
    itineraryTypeIdByQuery() {
      const { query } = this.$route;
      if (query.itinerary) return null;
      if (query.type) {
        const typeId = this.itineraryTypes.find(
          (type) => type.type.toLowerCase() === query.type
        );
        return typeId?.id;
      }
      return ITINERARY_TYPES.NO_TEMPLATE.id;
    },
    ...mapGetters({
      authData: "auth/getAuthData",
      clients: "client/getClients",
      currencies: "currency/getCurrencies",
      itinerary: "itinerary/getItinerary",
      disableQuoteByStatus: "itinerary/getDisableQuoteByStatus",
      hideElementByCondition: "itinerary/getHideElementByCondition",
      itineraryStatus: "itinerary/getItineraryStatus",
      client_contracts: "client/getClientContracts",
      roomPreferableDistributionJson:
        "itinerary/getRoomPreferableDistributionJson",
      loading: "utils/getLoading",
      languages: "language/getLanguages",
      isUserGuideActive: "userguide/getIsUserGuideActive",
      userGuideStep: "userguide/getStep",
      formItinerary: "itinerary/getFormItinerary",
      datesFormat: "itinerary/getDatesFormat",
      request: "request/getRequest",
      isProVersion: "auth/isProVersion",
      authConfig: "auth/authConfig",
    }),
    itineraryTypeLabel() {
      const currentItineraryTypeId = this.formItinerary?.[0]?.itinerary_type_id;
      return helper
        .getItineraryTypesMapped()
        ?.find((itineraryType) => itineraryType.id === currentItineraryTypeId)
        ?.name;
    },
  },
  props: {
    setLoading: {
      type: Function,
      default: () => {},
      required: true,
    },
    modalPreventBasicInfo: {
      type: Boolean,
      default: false,
    },
    handleModalPreventBasicInfo: {
      type: Function,
      default: () => {},
    },
    handleSaveItinerary: {
      type: Function,
      default: () => {},
      required: true,
    },
    loadClientContracts: {
      type: Function,
      default: () => {},
    },
    showServices: {
      type: Boolean,
      default: true,
    },
    service_types: {
      type: [Array, Object],
      default: () => [],
    },
    tripTypes: {
      type: [Array, Object],
      default: () => [],
    },
    setGlobalModalOpenName: {
      type: Function,
      default: () => {},
    },
    updateFormLanguage: {
      type: Function,
      default: () => {},
    },
    updateformItinerary: {
      type: Function,
      default: () => {},
    },
    updateContractLocations: {
      type: Function,
      default: () => {},
    },
  },
  mounted() {
    if (!this.itinerary.data?.id) {
      this.updateformItinerary(
        "itinerary_type_id",
        this.itineraryTypeIdByQuery
      );
    }
  },
  methods: {
    checkItineraryClient(clients) {
      if (!this.itinerary.data?.id && !this.formItinerary[0].client_id) {
        if (clients.length === 1) {
          this.updateClient(clients[0].id);
        }
      }
    },
    openSetAssigneeModal() {
      this.isSetAssigneeModalOpen = true;
    },
    closeSetAssigneeModal() {
      this.isSetAssigneeModalOpen = false;
    },
    preAssignQuoteToUser(user) {
      this.updateformItinerary("owner", {
        id: user.userId,
        name: user.userName,
      });

      this.closeSetAssigneeModal();
    },
    fieldHasError(field, key2 = null) {
      const value = key2
        ? this.formItinerary[0][field][key2]
        : this.formItinerary[0][field];
      const valueIsString = typeof value === "string";

      return (
        [null, undefined, ""].includes(value) || valueIsString?.length === 0
      );
    },
    validateTitleInput(event) {
      const validatedTitle = event.target.value.replace("|", "");
      this.updateformItinerary("title", {
        id: this.formItinerary[0].title.id,
        value: validatedTitle,
      });
      event.target.value = validatedTitle;
    },
    displayIsViewContractSalesManualModal() {
      this.$store.dispatch("utils/setIsViewContractSalesManualModalOpen", true);
    },
    handleValidateNDays($event) {
      if (Number($event.target.value) <= MAX_ITINERARY_DAYS) {
        this.days = $event;
        this.handleNumberDays($event);
      } else {
        this.days = MAX_ITINERARY_DAYS;
        $event.target.value = this.days;
        this.handleNumberDays($event);
        this.$buefy.toast.open({
          duration: 5000,
          message: `N° Days max is ${MAX_ITINERARY_DAYS}`,
          position: "is-top",
          type: "is-danger",
        });
      }
    },
    getMaxDateTo($event) {
      const endDate = new Date($event);
      endDate.setDate(endDate.getDate() + MAX_ITINERARY_DAYS);
      return endDate;
    },
    async loadFirstClientContract(clientId = "") {
      this.setLoading(true);
      await this.loadClientContracts(clientId);
      const firstClientContract = this.client_contracts[0];
      this.updateformItinerary("contract_id", firstClientContract?.id);
      this.setLoading(false);
    },
    isContractAvailableToSelect() {
      if (this.formItinerary[0].client_id === null) {
        this.$buefy.toast.open({
          duration: 5000,
          message: `Please select a Client before to select Contract.`,
          position: "is-top",
          type: "is-danger",
        });
        return;
      }
    },
    setDefaultRoomDistribution() {
      this.handleModalRoomDistribution();
      const defaultRoomDistribution = this.setDefaultRoomDistributionFromPax(
        this.roomPreferableDistributionJson,
        this.formItinerary[0].num_pax
      );
      this.$store.dispatch(
        "itinerary/setRoomPreferableDistributionJson",
        defaultRoomDistribution
      );
      this.setMaxRoomQty();
    },
    setDefaultRoomDistributionFromPax(data, numPax) {
      this.remainingPax = numPax;
      const roomQtyTypes = [...data];
      roomQtyTypes.sort((a, b) => b.max_pax - a.max_pax);
      const distribution = [];
      for (const roomType of roomQtyTypes) {
        const requiredRooms = Math.floor(this.remainingPax / roomType.max_pax);
        distribution.push({ ...roomType, qty: requiredRooms });
        this.remainingPax %= roomType.max_pax;
      }
      return distribution.sort((a, b) => a.max_pax - b.max_pax);
    },
    saveRoomPreferedDistribution() {
      this.handleModalRoomDistribution();
      if (this.formItinerary[0].id !== null) {
        this.handleSaveItinerary();
        this.updateContractLocations(this.formItinerary[0].contract_id, true);
      }
    },
    setMaxRoomQty() {
      const numPax = this.formItinerary[0].num_pax;
      if (numPax > 1) {
        let acumPax = 0;
        this.roomPreferableDistributionJson.forEach((item) => {
          acumPax += item.qty * item.max_pax;
        });
        this.remainingPax = numPax - acumPax;

        this.roomPreferableDistributionJson.forEach((room) => {
          let { id_room_type, qty, max_pax } = room;
          if (id_room_type === 1) {
            if (this.remainingPax + qty <= numPax) {
              room.max_qty = this.remainingPax + qty;
            } else {
              room.max_qty = numPax;
            }
          } else if (this.remainingPax >= max_pax) {
            room.max_qty = Math.ceil(this.remainingPax / max_pax) + qty;
          } else {
            room.max_qty = qty;
          }
        });
        this.$store.dispatch(
          "itinerary/setRoomPreferableDistributionJson",
          this.roomPreferableDistributionJson
        );
        this.$forceUpdate();
      }
    },
    handleModalRoomDistribution() {
      if (this.disableQuoteByStatus) return;
      if (this.hideElementByCondition) return;

      if (!this.formItinerary[0].num_pax) {
        this.$buefy.toast.open({
          duration: 5000,
          message: `Please select pax qty before to select room distribution.`,
          position: "is-top",
          type: "is-danger",
        });
        return;
      }
      this.modalRoomDistribution = !this.modalRoomDistribution;
      if (this.modalRoomDistribution) {
        this.setGlobalModalOpenName("modalRoomDistribution");
      } else {
        this.setGlobalModalOpenName("");
      }
    },
    handleModalShortcuts() {
      this.shortcutsOpen = !this.shortcutsOpen;
    },
    generateCode() {
      this.$store.dispatch("itinerary/generateCode");
    },
    handleNumberDays(e) {
      const days = Number(e.target.value) - 1;
      if (days >= 1) {
        const newDate = parse(this.formItinerary[0].start_date);
        newDate.setDate(newDate.getDate() + days);
        this.dateTo = format(newDate, DATE_FORMAT_YYYY_MM_DD);
      } else {
        this.days = 1;
        this.updateformItinerary("num_day", 1);
        this.dateTo = this.formItinerary[0].start_date;
      }
    },
    handleChangeDays(days) {
      this.dateFrom = this.formItinerary[0].start_date;
      if (this.formItinerary?.[0]?.num_day !== Math.ceil(days))
        this.updateformItinerary("num_day", Math.ceil(days));
    },
    focusedGuideBoxClass(classDefault, step, bgWhite = false) {
      const bgWhiteClass = bgWhite ? "bg-white" : "";
      return this.isUserGuideActive && this.userGuideStep === step
        ? `${classDefault} is-guide-step ${bgWhiteClass}`
        : classDefault;
    },
    nextStep() {
      if (this.isUserGuideActive) this.$store.dispatch("userguide/nextStep");
    },
    updateClient(clientId) {
      this.updateformItinerary("client_id", clientId);
      this.updateformItinerary("contract_id", null);
      this.generateCode();
      this.loadFirstClientContract(clientId);
    },
    prevStep() {
      if (this.isUserGuideActive) this.$store.dispatch("userguide/prevStep");
    },
    validateQuoteIsLinkedToRequest() {
      if (
        this.request?.id &&
        !this.itinerary?.data?.id &&
        !this.formItinerary[0].num_pax &&
        !this.formItinerary[0].num_pax &&
        !this.formItinerary[0].room_preferable_dist.length &&
        !this.formItinerary[0].start_date &&
        !this.formItinerary[0].end_date
      ) {
        this.$nextTick(() => {
          this.$refs.form_title__element.$el.children[1].focus();
        });
      }
    },
  },
};
</script>
<style>
.basic-info.shortcutLinkContainer .shortcutsLink {
  padding-right: 0.3em !important;
}
.roomDistributionCard {
  flex-direction: column;
}
.roomDistributionTitle,
.roomDistributionSubtitle {
  text-align: left;
  line-height: 40px;
}
.roomDistributionTitle {
  font-size: 30px;
  font-weight: 400;
  color: #32324d;
  margin-bottom: 1rem !important;
}
.roomDistributionSubtitle {
  font-size: 1rem;
  font-weight: 300;
  color: #32324d;
}
.roomDistributionBody {
  padding: 2rem 0;
  width: 90%;
  margin-top: 1.5rem;
  justify-content: space-evenly;
}
.roomDistributionBodyItem {
  flex-direction: column;
  padding: 1rem;
  border-radius: 0 0 0.8rem 0.8rem;
}
.roomDistributionBodyItem span {
  margin: 0.8rem 0;
  font-weight: 400;
}
.roomDistributionButtons {
  width: 60%;
  justify-content: space-between;
}
.roomDistributionButtons .field {
  width: 40%;
}
.quoteCreationSubtitle {
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: #666687;
}
.roomDistributionBodyItem[is-selected="active"] {
  border: 4px solid #f00b81;
  position: relative;
}
.roomDistributionBodyItem[is-selected="active"]::after {
  content: "Selected ✔";
  position: absolute;
  top: -2rem;
  width: 100%;
  border: 1px solid #f00b81;
  outline: 4px solid #f00b81;
  text-align: center;
  color: #fff;
  background-color: #f00b81;
  border-radius: 0.8rem 0.8rem 0 0;
  padding: 0.25rem 0;
}
.corner__icon {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  height: min-content;
  background-color: transparent;
}
.corner__icon button {
  background-color: transparent;
  border: none;
  cursor: pointer;
}
.itinerary_type_top__field {
  display: none !important;
}

.assignee_container {
  cursor: pointer;
}

.assignee_container > div {
  border: 1px solid #8e8ea9;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
}

.assignee_container > div[disabled] {
  cursor: not-allowed;
  background-color: #f5f5f5;
}

.BasicInfoForm .b-form-datepicker.base-datePicker.form-control .dropdown-menu {
  transform: none !important;
  top: 40px !important;
  left: 0px !important;
}

@media screen and (max-width: 480px) {
  .BasicInfoForm .animation-content {
    max-width: 90% !important;
    min-width: 90% !important;
    max-height: 90% !important;
    min-height: 90% !important;
    overflow-x: hidden;
  }
  .BasicInfoForm .animation-content .card {
    max-width: auto !important;
    min-width: auto !important;
    max-height: 100% !important;
    min-height: 100% !important;
  }
  .roomDistributionTitle {
    font-size: 1.5rem;
    line-height: 1.5rem;
  }
  .roomDistributionSubtitle {
    font-size: 0.85rem;
    line-height: 1.5rem;
    font-weight: 400;
  }
  .roomDistributionBody {
    flex-direction: column;
    gap: 2rem;
  }
  .roomDistributionBodyItem {
    border: 1px solid #ccc;
    border-radius: 1rem;
  }
  .roomDistributionBodyItem[is-selected="active"] {
    border-radius: 0 0 1rem 1rem;
    margin-top: 2rem;
  }
  .roomDistributionButtons {
    gap: 1rem;
    align-items: center;
    width: 100%;
  }
  .roomDistributionButtons .field {
    width: auto;
    margin: 0;
  }
  .card-footer {
    position: sticky;
    bottom: 0;
    left: 0;
    background-color: #fff;
  }
  .quoteCreationSubtitle {
    display: block;
    margin: 0.25rem 0 1rem;
  }
  .BasicInfoForm {
    padding: 1.25rem !important;
    margin-top: 0rem !important;
  }
  .BasicInfoForm .columns {
    margin: 0.2rem 0;
  }
  .BasicInfoForm .column {
    padding: 0.5rem 0;
  }
  .BasicInfoForm > div > h2 {
    padding: 0 !important;
    margin: 0 !important;
  }
  .selectContainer .label,
  .base-input-container .label {
    font-size: 0.8rem !important;
  }
  .itinerary_type_top__field {
    display: flex !important;
    margin: 0rem !important;
  }
  .basic_info_form__footer,
  .itinerary_type_bottom__field {
    display: none !important;
  }
  .shortcutsLink {
    display: none;
  }
  .columns:not(:last-child) {
    margin-bottom: 0rem;
  }
}
</style>
